import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Star,
  ThumbsUp,
  ExternalLink,
  Loader2,
  MessageSquare,
  CheckCircle,
} from 'lucide-react';
import { sanitizeUrl } from './utils/sanitize';

/**
 * ReviewPage — Customer-facing landing page for /review?r={receipt_id}
 *
 * COMPLIANCE NOTE — Option B / Equal Pathways
 * --------------------------------------------
 * Every customer, regardless of their star rating, is shown the SAME two
 * follow-up options after rating: leave private feedback, or post a
 * public Google review. No sentiment-based branching. No UX
 * differentiation by score. This is the equal-pathways pattern used by
 * Podium, Birdeye, BrightLocal, Reputation.com, and GatherUp to comply
 * with:
 *
 *   - Google Maps User-Generated Content Policy (April 2026 update)
 *     prohibiting review gating / selective solicitation of positive
 *     reviews.
 *   - FTC Consumer Review Rule, 16 CFR Part 465, which prohibits
 *     using software to filter out negative reviews before they are
 *     posted (civil penalties up to ~$53k per violation).
 *
 * Do NOT reintroduce sentiment-based UI logic here. Differential prominence,
 * conditional visibility, or "smart" UX that nudges high-raters toward
 * Google and low-raters toward private feedback all count as gating
 * under the current rules — the policy targets the OUTCOME, not just
 * the technical implementation.
 *
 * Internal merchant-facing categorization of ratings (e.g. ReviewsTab's
 * "Needs response" filter) is fine. Customer-facing differentiation by
 * sentiment is not.
 */

const ReviewPage = () => {
  const [searchParams] = useSearchParams();
  const receiptId = searchParams.get('r');

  // State loaded from backend - NO PII in URL
  const [customerName, setCustomerName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [googleReviewUrl, setGoogleReviewUrl] = useState('');
  const [storeSlug, setStoreSlug] = useState('');

  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  // Steps: loading | rating | actions | thanks | error
  // The previous 'feedback' and 'google' steps (which gated by rating) have
  // been merged into a single 'actions' step that shows BOTH options to
  // every customer regardless of score.
  const [step, setStep] = useState('loading');
  const [error, setError] = useState(null);

  // Per-action submission state. Each option can be acted on independently —
  // the customer can do one, both, or neither.
  const [savingRating, setSavingRating] = useState(false);
  const [feedbackState, setFeedbackState] = useState('idle');  // idle | submitting | done
  const [googleState, setGoogleState] = useState('idle');      // idle | submitting | done

  // Fetch customer info from receipt_id on mount
  useEffect(() => {
    const loadReceiptInfo = async () => {
      if (!receiptId) {
        setError('Invalid review link');
        setStep('error');
        return;
      }

      try {
        const response = await fetch(`/api/review-feedback?r=${receiptId}`);
        const data = await response.json();

        if (!data.success) {
          setError(data.error || 'Could not load review page');
          setStep('error');
          return;
        }

        setCustomerName(data.customer_name || 'Customer');
        setBusinessName(data.business_name || 'our store');
        setGoogleReviewUrl(sanitizeUrl(data.google_review_url) || '');
        setStoreSlug(data.store_slug || '');
        setStep('rating');

      } catch (err) {
        console.error('Error loading receipt:', err);
        setError('Could not load review page');
        setStep('error');
      }
    };

    loadReceiptInfo();
  }, [receiptId]);

  // Auto-advance to thanks once both available actions are done.
  // Lives in an effect so we don't queue setState from inside render.
  useEffect(() => {
    if (step !== 'actions') return;
    const googleDoneOrUnavailable = googleState === 'done' || !googleReviewUrl;
    if (feedbackState === 'done' && googleDoneOrUnavailable) {
      const timer = setTimeout(() => setStep('thanks'), 1200);
      return () => clearTimeout(timer);
    }
  }, [step, feedbackState, googleState, googleReviewUrl]);

  // ==========================================
  // ACTIONS
  // ==========================================
  const handleRatingSelect = async (selectedRating) => {
    if (savingRating || rating > 0) return;
    setRating(selectedRating);
    setSavingRating(true);

    // Save rating to backend (fire-and-await; if it fails we still advance
    // to the actions view — the customer's experience shouldn't break on a
    // transient network error).
    try {
      await fetch('/api/review-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'save_rating',
          receipt_id: receiptId,
          rating: selectedRating,
          customer_name: customerName
        })
      });
    } catch (err) {
      console.error('Error saving rating:', err);
    } finally {
      setSavingRating(false);
      // Short delay so the selected stars register visually before transitioning
      setTimeout(() => setStep('actions'), 400);
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!feedback.trim() || feedbackState !== 'idle') return;
    setFeedbackState('submitting');

    try {
      const resp = await fetch('/api/review-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'save_feedback',
          receipt_id: receiptId,
          rating: rating,
          feedback: feedback,
          customer_name: customerName
        })
      });
      const data = await resp.json();

      if (data.success) {
        setFeedbackState('done');
      } else {
        console.error('save_feedback failed:', data.error);
        setFeedbackState('idle');
      }
    } catch (err) {
      console.error('Error saving feedback:', err);
      setFeedbackState('idle');
    }
  };

  const handleGoogleClick = async () => {
    if (googleState !== 'idle' || !googleReviewUrl) return;
    setGoogleState('submitting');

    // Fire the tracking call but don't block the redirect on it. Open
    // Google in a new tab so the customer can return to this page and
    // still leave private feedback if they want to do both.
    try {
      fetch('/api/review-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'track_google_click',
          receipt_id: receiptId,
          rating: rating,
          customer_name: customerName
        })
      }).catch((err) => console.error('track_google_click error:', err));

      window.open(googleReviewUrl, '_blank', 'noopener,noreferrer');
      setGoogleState('done');
    } catch (err) {
      console.error('Error tracking click:', err);
      setGoogleState('idle');
    }
  };

  // ==========================================
  // RENDER
  // ==========================================

  // Loading state
  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (step === 'error') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Link Expired or Invalid</h2>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  // Rating step
  if (step === 'rating') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Hi {customerName}!
            </h1>
            <p className="text-slate-600">
              How was your experience with {businessName}?
            </p>
          </div>

          <div className="flex justify-center gap-2 mb-6">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => handleRatingSelect(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                disabled={savingRating || rating > 0}
                aria-label={`${star} star${star === 1 ? '' : 's'}`}
                className="p-2 transition-transform hover:scale-110 active:scale-95 disabled:cursor-default"
              >
                <Star
                  className={`w-12 h-12 ${
                    star <= (hoveredRating || rating)
                      ? 'text-amber-400 fill-current'
                      : 'text-slate-300'
                  }`}
                />
              </button>
            ))}
          </div>

          <p className="text-center text-sm text-slate-500">
            {savingRating ? 'Saving your rating…' : 'Tap a star to rate your experience'}
          </p>
        </div>
      </div>
    );
  }

  // Actions step (Option B equal pathways)
  // CRITICAL: this view renders the SAME options with the SAME prominence
  // regardless of `rating`. Do not introduce conditional UX based on the
  // score — that would be gating. See the file header.
  if (step === 'actions') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={`w-5 h-5 ${
                    n <= rating ? 'text-amber-400 fill-current' : 'text-slate-300'
                  }`}
                />
              ))}
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              Thanks for the rating!
            </h2>
            <p className="text-slate-600 text-sm">
              Would you like to share more? Both options are up to you.
            </p>
          </div>

          <div className="space-y-3">
            {/* OPTION 1: Private feedback to merchant */}
            <ActionCard
              icon={MessageSquare}
              title="Share your feedback"
              subtitle={`A private message to the ${businessName} team`}
              state={feedbackState}
              doneLabel="Feedback sent — thank you"
            >
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="What stood out? What could we do better?"
                rows={4}
                disabled={feedbackState !== 'idle'}
                className="w-full p-3 border-2 border-slate-200 rounded-lg resize-none text-sm focus:border-blue-500 focus:outline-none disabled:opacity-60 disabled:bg-slate-50"
              />
              <button
                onClick={handleFeedbackSubmit}
                disabled={!feedback.trim() || feedbackState !== 'idle'}
                className="mt-2 w-full py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {feedbackState === 'submitting' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Sending…</span>
                  </>
                ) : (
                  <span>Send feedback</span>
                )}
              </button>
            </ActionCard>

            {/* OPTION 2: Public Google review — same visual weight as Option 1.
                Hidden ONLY if the merchant hasn't configured a Google review URL
                (i.e. the path is unavailable for everyone, not gated by score). */}
            {googleReviewUrl && (
              <ActionCard
                icon={ExternalLink}
                title="Leave a Google review"
                subtitle="Help other shoppers find quality service"
                state={googleState}
                doneLabel="Thanks for sharing publicly"
              >
                <button
                  onClick={handleGoogleClick}
                  disabled={googleState !== 'idle'}
                  className="w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {googleState === 'submitting' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Opening…</span>
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4" />
                      <span>Open Google review</span>
                    </>
                  )}
                </button>
              </ActionCard>
            )}
          </div>

          <p className="text-[11px] text-slate-400 text-center mt-6 leading-relaxed">
            Both options are independent — you can do one, both, or neither.
          </p>
        </div>
      </div>
    );
  }

  // Thanks step
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <ThumbsUp className="w-8 h-8 text-emerald-600" />
        </div>

        <h2 className="text-xl font-bold text-slate-900 mb-2">
          Thank You!
        </h2>
        <p className="text-slate-600">
          We appreciate your feedback, {customerName}!
        </p>
      </div>
    </div>
  );
};

// ==========================================
// PRIMITIVE
// ==========================================

const ActionCard = ({ icon: Icon, title, subtitle, state, doneLabel, children }) => {
  const isDone = state === 'done';
  return (
    <div
      className={`rounded-xl border p-4 transition-all ${
        isDone ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
      }`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isDone
              ? 'bg-emerald-100 text-emerald-600'
              : 'bg-white border border-slate-200 text-slate-600'
          }`}
        >
          {isDone ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          <p className="text-xs text-slate-500 mt-0.5">{isDone ? doneLabel : subtitle}</p>
        </div>
      </div>
      {!isDone && children}
    </div>
  );
};

export default ReviewPage;